/**
 * cart/validate.ts — Pure cart validation logic.
 *
 * T7.S6.13 — Cart edge cases (max qty, sold-out, price drift).
 *
 * This module is the single source of truth for whether a cart line is
 * checkout-ready. It is called from:
 *   - POST /api/cart/validate (server-side revalidation before checkout)
 *   - POST /api/checkout (defence-in-depth — Stripe session amount uses validated prices)
 *
 * It is intentionally I/O-free: callers fetch the current catalog and hand
 * the snapshot in. That keeps it trivially unit-testable and lets us reuse
 * the same logic across endpoints without hidden coupling to Hasura.
 *
 * Public types:
 *   CartLineInput       — what a client sends (subset of CartLine)
 *   CartLineDecision    — per-line verdict, including any old/new price drift
 *   CartValidation      — aggregate verdict for the whole cart
 */

import { MAX_QTY_PER_LINE } from './constants'

export interface CartLineInput {
  productId: string
  sku: string
  variantId: string | null
  variantLabel?: string | null
  name?: string
  unitPriceCents: number
  quantity: number
}

export interface CatalogVariantSnapshot {
  variant_id: string
  is_enabled: boolean
  price_cents: number
}

export interface CatalogProductSnapshot {
  id: string
  status: 'draft' | 'active' | 'archived'
  price_cents: number
  variants: CatalogVariantSnapshot[]
}

export type CartLineIssue =
  | 'ok'
  | 'qty_clamped'
  | 'product_missing'
  | 'product_inactive'
  | 'variant_missing'
  | 'sold_out'
  | 'price_changed'

export interface CartLineDecision {
  productId: string
  variantId: string | null
  sku: string
  /** The (clamped + valid) quantity that should be sent to Stripe. */
  quantity: number
  /** The price the line was last persisted with on the client. */
  cartUnitPriceCents: number
  /** The current authoritative catalog price. */
  currentUnitPriceCents: number
  /** Detail buckets — independent flags, not an enum. */
  issues: CartLineIssue[]
  /** Convenience flags. */
  qtyClamped: boolean
  priceChanged: boolean
  soldOut: boolean
  removable: boolean
  /** Optional human reason for UI display. */
  reason: string | null
}

export interface CartValidation {
  ok: boolean
  /** True when EVERY surviving line is ok and no prices drifted. */
  checkoutReady: boolean
  /** Lines the user should keep — with possibly clamped qty / new price. */
  lines: CartLineDecision[]
  /** Lines fully removed (sold-out or product gone). */
  removed: CartLineDecision[]
  /** True if any line's price moved (up or down). */
  priceDrift: boolean
  /** True if any line was clamped down on quantity. */
  qtyClamped: boolean
  /** Subtotal in cents using current catalog prices and clamped qty. */
  subtotalCents: number
}

/**
 * Clamp a raw quantity into the legal 1..MAX_QTY_PER_LINE range.
 * Non-numeric, NaN, or sub-1 values clamp to 1.
 */
export function clampQuantity(raw: number): number {
  if (!Number.isFinite(raw)) return 1
  const n = Math.floor(raw)
  if (n < 1) return 1
  if (n > MAX_QTY_PER_LINE) return MAX_QTY_PER_LINE
  return n
}

/**
 * Validate a single cart line against the catalog snapshot.
 *
 * Pure. Does no I/O. Idempotent — calling twice with the same inputs returns
 * the same decision.
 */
export function validateLine(
  line: CartLineInput,
  catalog: Map<string, CatalogProductSnapshot>,
): CartLineDecision {
  const issues: CartLineIssue[] = []
  const clamped = clampQuantity(line.quantity)
  const qtyClamped = clamped !== line.quantity

  const product = catalog.get(line.productId)
  if (!product) {
    return {
      productId: line.productId,
      variantId: line.variantId,
      sku: line.sku,
      quantity: clamped,
      cartUnitPriceCents: line.unitPriceCents,
      currentUnitPriceCents: line.unitPriceCents,
      issues: ['product_missing'],
      qtyClamped,
      priceChanged: false,
      soldOut: true,
      removable: true,
      reason: 'This product is no longer available.',
    }
  }

  if (product.status !== 'active') {
    return {
      productId: line.productId,
      variantId: line.variantId,
      sku: line.sku,
      quantity: clamped,
      cartUnitPriceCents: line.unitPriceCents,
      currentUnitPriceCents: product.price_cents,
      issues: ['product_inactive'],
      qtyClamped,
      priceChanged: false,
      soldOut: true,
      removable: true,
      reason: 'This product is no longer available.',
    }
  }

  // Variants are optional — products without variants pass through using the
  // product-level price. With variants, the picked variant must exist and be
  // enabled to be sellable.
  let currentPrice = product.price_cents
  let variant: CatalogVariantSnapshot | null = null
  if (line.variantId) {
    variant =
      product.variants.find((v) => v.variant_id === line.variantId) ?? null
    if (!variant) {
      return {
        productId: line.productId,
        variantId: line.variantId,
        sku: line.sku,
        quantity: clamped,
        cartUnitPriceCents: line.unitPriceCents,
        currentUnitPriceCents: product.price_cents,
        issues: ['variant_missing'],
        qtyClamped,
        priceChanged: false,
        soldOut: true,
        removable: true,
        reason: 'This variant is no longer offered.',
      }
    }
    if (!variant.is_enabled) {
      return {
        productId: line.productId,
        variantId: line.variantId,
        sku: line.sku,
        quantity: clamped,
        cartUnitPriceCents: line.unitPriceCents,
        currentUnitPriceCents: variant.price_cents || product.price_cents,
        issues: ['sold_out'],
        qtyClamped,
        priceChanged: false,
        soldOut: true,
        removable: true,
        reason: 'Out of stock.',
      }
    }
    if (variant.price_cents > 0) {
      currentPrice = variant.price_cents
    }
  }

  const priceChanged = currentPrice !== line.unitPriceCents

  if (qtyClamped) issues.push('qty_clamped')
  if (priceChanged) issues.push('price_changed')
  if (issues.length === 0) issues.push('ok')

  return {
    productId: line.productId,
    variantId: line.variantId,
    sku: line.sku,
    quantity: clamped,
    cartUnitPriceCents: line.unitPriceCents,
    currentUnitPriceCents: currentPrice,
    issues,
    qtyClamped,
    priceChanged,
    soldOut: false,
    removable: false,
    reason: priceChanged
      ? currentPrice > line.unitPriceCents
        ? 'Price increased.'
        : 'Price decreased.'
      : null,
  }
}

/**
 * Validate a whole cart and return a structured verdict.
 *
 * The aggregate `checkoutReady` flag is true only when every surviving line
 * is ok and no prices changed since the cart was loaded. UI uses that flag
 * to enable / disable the checkout button.
 */
export function validateCart(
  lines: CartLineInput[],
  catalog: Map<string, CatalogProductSnapshot>,
): CartValidation {
  const decisions = lines.map((l) => validateLine(l, catalog))
  const survivors = decisions.filter((d) => !d.removable)
  const removed = decisions.filter((d) => d.removable)
  const priceDrift = survivors.some((d) => d.priceChanged)
  const qtyClamped = survivors.some((d) => d.qtyClamped)
  const subtotalCents = survivors.reduce(
    (acc, d) => acc + d.currentUnitPriceCents * d.quantity,
    0,
  )

  return {
    ok: removed.length === 0,
    checkoutReady:
      removed.length === 0 &&
      !priceDrift &&
      survivors.length > 0 &&
      survivors.every((d) => d.issues.length === 1 && d.issues[0] === 'ok'),
    lines: survivors,
    removed,
    priceDrift,
    qtyClamped,
    subtotalCents,
  }
}

/**
 * Build a catalog snapshot map from the raw vet_merch_products rows.
 *
 * Accepts the shape we already store in Hasura: `variants` is a jsonb array
 * with the cost/price/is_enabled fields. We only project what we need.
 */
type RawVariant = { variant_id: string; price_cents?: number; is_enabled?: boolean }

/**
 * Hasura returns jsonb columns (e.g. `variants`) as JSON *strings*, not parsed
 * arrays — same as products.ts normalizeProduct. Coerce to an array here so a
 * stringified payload never blows up `.map` (which previously threw, emptied
 * the whole snapshot, and made every product report "no longer available",
 * blocking all premade checkout).
 */
function coerceVariants(value: unknown): RawVariant[] {
  if (Array.isArray(value)) return value as RawVariant[]
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value)
      return Array.isArray(parsed) ? (parsed as RawVariant[]) : []
    } catch {
      return []
    }
  }
  return []
}

export function buildCatalogSnapshot(
  rows: Array<{
    id: string
    status: string
    price_cents: number
    variants: RawVariant[] | string | null
  }>,
): Map<string, CatalogProductSnapshot> {
  const map = new Map<string, CatalogProductSnapshot>()
  for (const r of rows) {
    map.set(r.id, {
      id: r.id,
      status: (r.status as CatalogProductSnapshot['status']) ?? 'archived',
      price_cents: r.price_cents,
      variants: coerceVariants(r.variants).map((v) => ({
        variant_id: String(v.variant_id),
        price_cents: typeof v.price_cents === 'number' ? v.price_cents : 0,
        is_enabled: v.is_enabled !== false, // default true if unset
      })),
    })
  }
  return map
}
